import React, { useEffect, useState } from 'react';
import { Button, Chip, Divider, Typography } from '@material-ui/core';
import { useLibrary } from '../hooks/useLibrary';
import { useTemplates } from '../hooks/useTemplates';
import { EeTable } from 'components/EeTable/EeTable';
import { EeInput } from 'components/EeInput/EeInput';
import { EeSelect } from 'components/EeSelect/EeSelect';
import { KPIDrawer } from './KPIDrawer';
import { observer } from 'mobx-react-lite';
import { EeModal } from 'components/EeModal/EeModal';
import { ApolloLink } from '@apollo/client/link/core';
import { EeLoadingArea } from 'components/EeLoadingArea';
import { KPI } from 'types/kpi';
// @ts-ignore
import { IBaseResponse } from 'types/common';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'hooks/useSnackbar';

// FixMe
const ACCOUNT_ID = 'account id';

export interface LibraryPageProps {
  apiBaseURL: ApolloLink;
}

export const LibraryPage = observer(() => {
  const { operations, models } = useLibrary();
  const { operations: tmplOperations, models: tmplModels } = useTemplates();
  const [isEditMode, setIsEditMode] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingRow, setDeletingRow] = useState({
    id: 0,
    name: '',
    templates: [] as any[],
  });

  const columns = React.useMemo(
    () => [
      {
        Header: 'KPIs',
        accessor: 'name',
        Cell: ({ value }: any) => <Chip label={value} />,
      },
      {
        Header: 'Groups',
        accessor: 'group',
      },
      {
        Header: 'Type',
        accessor: 'type',
      },
      {
        Header: 'Description',
        accessor: 'description',
      },
    ],
    [],
  );

  const { addSnackbar } = useSnackbar();

  useEffect(() => {
    tmplOperations.loadTemplates(ACCOUNT_ID);
    operations.loadKPIs(ACCOUNT_ID).then();
    operations.loadTypes();
  }, []);

  const handleSave = async (isEditMode: boolean, kpis: KPI[]) => {
    const result = await operations.kpi.saveEditorKPIs(isEditMode, kpis);
    if (result.errorCode === 0) {
      await operations.loadKPIs(ACCOUNT_ID);

      const kpiNames = kpis.map((kpi) => kpi.name).join(', ');
      const content = isEditMode
        ? `KPI Updated: ${kpiNames} was successfully saved.`
        : `New KPI: ${kpiNames} ${
            kpis.length === 1 ? 'was' : 'were'
          } successfully created.`;
      addSnackbar(content);
      operations.closeKPIDrawer();
    } else {
      addSnackbar('Failed to create KPI');

      operations.closeKPIDrawer();
    }
  };

  const handleDelete = async () => {
    const response: IBaseResponse = await operations.kpi.deleteKPI(
      deletingRow.id.toString(),
    );
    if (response.errorCode === 0) {
      setModalOpen(false);
      operations.loadKPIs(ACCOUNT_ID).then();
    }
  };

  return (
    <div className="bg-gray w-100 mx-auto px-3" style={{ maxWidth: 1200 }}>
      <Typography variant="h1" className="my-3">
        KPI Library
      </Typography>
      <div className="d-flex flex-wrap lens-align-bottom">
        <EeInput
          value={models.search.value}
          search
          onChange={operations.handleSearch}
          type="search"
          className="mr-3"
          notFullWidth
          placeholder="Search"
        />
        <div className="d-flex align-items-center flex-grow-1 mr-3 lens-input-warper lens-ml-2 lens-align-start">
          <span className="mr-2 lens-ee-label">Filter by</span>
          <EeSelect
            items={[{ id: 'All Groups', name: 'All Groups' }, ...models.groups]}
            value={models.search.groupId}
            onChange={operations.handleGroupsChange}
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setIsEditMode(false);
            operations.openNewKPIDrawer();
          }}
        >
          CREATE NEW KPI
        </Button>
      </div>
      <Divider />
      {models.loading && <EeLoadingArea />}
      {!models.loading && (
        <EeTable
          columns={columns}
          data={models.kpis}
          onRowEdit={({ row }) => {
            setIsEditMode(true);
            operations.openEditKPIDrawer(row);
          }}
          onRowDelete={({ row }: any) => {
            setModalOpen(true);
            const { id, name } = row.original;
            const templates = tmplModels.templates.filter((t) =>
              t.indicators?.some(
                ({ id: indicatorId }: any) => indicatorId === id,
              ),
            );
            setDeletingRow({ id, name, templates });
          }}
        />
      )}
      <KPIDrawer
        open={models.openKPIDrawer}
        isEditMode={isEditMode}
        onClose={operations.closeKPIDrawer}
        onSave={handleSave}
        groupOptions={models.groups.map(({ name }) => name)}
        typeOptions={models.types}
        onKPIEditorBlur={(editorKPI: object) =>
          operations.editor.editorKPIChange(editorKPI)
        }
      />
      <EeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={`Delete KPI - ${deletingRow.name}`}
        actions={
          <div className="d-flex align-items-center w-100">
            <Button
              onClick={() => setModalOpen(false)}
              variant="outlined"
              color="primary"
              className="ml-auto mr-3"
            >
              Cancel
            </Button>
            {deletingRow.templates.length > 0 ? (
              <Button
                onClick={() => setModalOpen(false)}
                variant="contained"
                color="primary"
              >
                Okay
              </Button>
            ) : (
              <Button
                onClick={handleDelete}
                variant="contained"
                color="primary"
              >
                Delete
              </Button>
            )}
          </div>
        }
      >
        {deletingRow.templates.length > 0 ? (
          <div>
            <p>
              If you want to delete the <strong>{deletingRow.name}</strong> KPI,
              please remove it from the following templates first:
            </p>
            <ol>
              {deletingRow.templates.map((template: any) => (
                <li key={template.id}>
                  <Link to="/kpis/templates">{template.name}</Link>
                </li>
              ))}
            </ol>
          </div>
        ) : (
          <p>
            Do you want to delete the <strong>{deletingRow.name}</strong> KPI?
          </p>
        )}
      </EeModal>
    </div>
  );
});
